<template>
  <div class="h-100 d-flex flex-column">
    <div
      class="mb-5 d-flex justify-content-between align-items-center"
    >
      <h3
        class="mb-0"
      >
        Custom Properties
      </h3>
      <b-button
        variant="primary"
        :class="{'btn-loading': isLoadingSaveButton}"
        @click="saveCustomProperties()"
        :disabled="!canSaveChanges"
        data-test="custom-properties-submit-button"
      >
        Save
      </b-button>
    </div>
    <div
      :class="['flex-grow-1', {'d-flex align-items-center justify-content-center': isLoadingProperties}]"
    >
      <div
        v-if="isLoadingProperties"
        class="loader"
      />
      <div
        v-else
      >
        <div
          v-for="(property, index) in properties"
          :key="index"
          :class="[{'mb-3': index < properties.length -1}]"
        >
          <div
            class="d-flex justify-content-between align-items-center"
          >
            <p
              class="mb-0"
            >
              <strong>
                Property {{ index + 1 }}
              </strong>
            </p>
            <b-button
              v-if="index < properties.length -1 || property.type !== ''"
              variant="danger"
              size="sm"
              class="m-0 py-2 px-3"
              @click="deleteProperty(index)"
              :disabled="GET_IS_CAMPAIGN_READONLY"
              :data-test="`custom-property-${index}-delete-btn`"
            >
              <i class="fal fa-trash-can" />
            </b-button>
          </div>
          <base-select
            class="my-2"
            :options="types"
            :value="property.type"
            :placeholder="'Property'"
            @change="selectPropertyType($event.value, index)"
            @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
            @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
            :disabled="GET_IS_CAMPAIGN_READONLY"
            :data-test="`custom-property-${index}-type`"
          />
          <b-form-input
            :value="property.value"
            @input="setPropertyValue($event, index)"
            @focus="SET_IS_SIDEBAR_INPUT_FOCUSED(true)"
            @blur="SET_IS_SIDEBAR_INPUT_FOCUSED(false)"
            placeholder="Value"
            :disabled="GET_IS_CAMPAIGN_READONLY"
            :data-test="`custom-property-${index}-value`"
          />
        </div>
        <b-button
          ref="add-custom-property-btn"
          variant="tertiary"
          class="mt-3 mb-5 d-flex align-items-center"
          @click="addNewProperty()"
          :disabled="GET_IS_CAMPAIGN_READONLY"
          data-test="add-custom-property-btn"
        >
          <p
            class="mb-0"
          >
            Add Property
          </p>
          <i class="fal fa-plus ml-1" />
        </b-button>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';

import helper from '@/core/helper';

import BaseSelect from '@/components/BaseSelect';

export default {
  name: 'CustomProperties',
  components: {
    BaseSelect,
  },
  data() {
    return {
      properties: [],
      isLoadingProperties: false,
      isLoadingSaveButton: false,
      isDirty: false,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'selectedAdvertiser',
    ]),
    ...mapState('content', [
      'selectedArticleUuid',
      'selectedCreativeId',
      'feedId',
      'customPropertyTypes',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_CAMPAIGN_READONLY,
    ]),
    ...mapGetters('content', [
      contentTypes.GET_CONTENT_ITEM_DISPLAY_PROPERTIES,
    ]),
    types() {
      return this.customPropertyTypes.filter((type) => type);
    },
    selectedCreativeTemplateProperties() {
      return this.GET_CONTENT_ITEM_DISPLAY_PROPERTIES(this.selectedArticleUuid, this.selectedCreativeId);
    },
    selectedCreativeArticleCombo() {
      return `${this.selectedCreativeId}-${this.selectedArticleUuid}`;
    },
    canSaveChanges() {
      return !this.GET_IS_CAMPAIGN_READONLY && this.isDirty;
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.FETCH_CUSTOM_PROPERTY_TYPES,
      contentTypes.UPDATE_CONTENT_DISPLAY_COPY,
      contentTypes.SET_IS_SIDEBAR_INPUT_FOCUSED,
    ]),
    async addNewProperty() {
      if (this.GET_IS_CAMPAIGN_READONLY) return;
      this.properties.push({
        type: '',
        value: '',
      });
      await this.$nextTick();
      this.$refs['add-custom-property-btn'].scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'end' });
    },
    deleteProperty(index) {
      this.properties.splice(index, 1);
      if (this.properties.length === 0) this.addNewProperty();
      this.isDirty = true;
    },
    selectPropertyType(type, index) {
      this.properties[index].type = type;
      this.isDirty = true;
    },
    setPropertyValue: helper.debounce(function setPropertyValue(value, index) {
      this.properties[index].value = value;
      this.isDirty = true;
    }, 200),
    async saveCustomProperties() {
      if (!this.canSaveChanges) return;
      this.isLoadingSaveButton = true;
      const requestPayload = {
        advertisersId: this.selectedAdvertiser.id,
        creativeId: this.selectedCreativeId,
        feedId: this.feedId,
        id: this.selectedArticleUuid,
        body: {},
      };
      this.properties.forEach((property) => {
        if (property.type) {
          requestPayload.body[property.type] = property.value;
        }
      });
      this.types.forEach((type) => {
        if (!requestPayload.body.hasOwnProperty(type)) requestPayload.body[type] = null;
      });
      await this.UPDATE_CONTENT_DISPLAY_COPY(requestPayload);
      this.isLoadingSaveButton = false;
    },
    populateProperties() {
      this.isLoadingProperties = true;
      this.properties = [];
      this.types.forEach((type) => {
        if (this.selectedCreativeTemplateProperties.hasOwnProperty(type)) {
          this.properties.push({
            type,
            value: this.selectedCreativeTemplateProperties[type],
          });
        }
      });
      if (this.properties.length === 0) {
        this.properties.push({
          type: '',
          value: '',
        });
      }
      this.isLoadingProperties = false;
    },
  },
  mounted() {
    this.isLoadingProperties = true;
    this.FETCH_CUSTOM_PROPERTY_TYPES()
      .then(() => {
        this.populateProperties();
      });
  },
  watch: {
    selectedCreativeArticleCombo(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.populateProperties();
      }
    },
  },
};
</script>
<style scoped lang="scss">
::v-deep .custom-select {
  option {
    color: $gray-700 !important;
  }
}
.btn {
  min-width: unset;
}
</style>
